import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PanelLeft, MessageSquare, FolderTree, FileText } from 'lucide-react'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { ResizableDivider } from '@/features/develop/components/ResizableDivider'
import { useVaultStore } from '@/stores/useVaultStore'
import { resolveVaultNode } from '@/lib/api'
import { VaultTree } from './VaultTree'
import { VaultNodePage } from './VaultNodePage'
import { ConciergeRail } from './ConciergeRail'

/**
 * /vault — the three-pane workspace (Obsidian-replacement). Tree · Page ·
 * Concierge. Desktop: resizable, collapsible rails (drag the dividers; widths
 * persist). Mobile: one pane at a time + bottom tab nav. The three panes are
 * memoized elements so dragging a divider re-renders only the layout, not the
 * tree/page/chat.
 */
const MIN_CENTER = 400 // the page pane never shrinks below this on desktop

export function VaultPage() {
  // Below this, three panes get cramped — drop to single-pane (mobile) layout.
  const isNarrow = useMediaQuery('(max-width: 1079px)')
  const treeOpen = useVaultStore((s) => s.treeOpen)
  const setTreeOpen = useVaultStore((s) => s.setTreeOpen)
  const conciergeOpen = useVaultStore((s) => s.conciergeOpen)
  const setConciergeOpen = useVaultStore((s) => s.setConciergeOpen)
  const selectedNodeId = useVaultStore((s) => s.selectedNodeId)
  const treeWidth = useVaultStore((s) => s.treeWidth)
  const conciergeWidth = useVaultStore((s) => s.conciergeWidth)
  const setTreeWidth = useVaultStore((s) => s.setTreeWidth)
  const setConciergeWidth = useVaultStore((s) => s.setConciergeWidth)

  // Active mobile pane lives in the store so the editor toolbar (rendered in a
  // portal) can tell which tab is showing and not bleed onto the others.
  const pane = useVaultStore((s) => s.pane)
  const setPane = useVaultStore((s) => s.setPane)
  const firstRender = useRef(true)
  // On narrow (single-pane) layouts, picking something jumps to the page pane.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (isNarrow && selectedNodeId) setPane('page')
  }, [selectedNodeId, isNarrow])

  // Deep-link: /vault?block=<encoded folder-note path>. Calendar event
  // descriptions link here; resolve the path to a node, open it in the center
  // pane, and strip the param so a refresh doesn't re-trigger.
  useEffect(() => {
    const block = new URLSearchParams(window.location.search).get('block')
    if (!block) return
    let cancelled = false
    void (async () => {
      const node = await resolveVaultNode(block)
      if (cancelled || !node) return
      // Reveal it in the tree (expand the full ancestor path) + open in center.
      useVaultStore.getState().requestReveal(node)
    })()
    const url = new URL(window.location.href)
    url.searchParams.delete('block')
    window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    return () => {
      cancelled = true
    }
  }, [])

  const [winW, setWinW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440))
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Stable elements — a divider drag re-renders VaultPage (width) but reuses
  // these, so React skips re-rendering the panes themselves.
  const treeEl = useMemo(() => <VaultTree />, [])
  const pageEl = useMemo(() => <VaultNodePage />, [])
  const railEl = useMemo(() => <ConciergeRail />, [])

  // Resize handlers cap each rail so the center can't drop below MIN_CENTER.
  const onTreeResize = useCallback((d: number) => {
    const st = useVaultStore.getState()
    const cap = window.innerWidth - MIN_CENTER - (st.conciergeOpen ? st.conciergeWidth : 0)
    setTreeWidth(Math.min(st.treeWidth + d, cap))
  }, [setTreeWidth])
  const onConciergeResize = useCallback((d: number) => {
    const st = useVaultStore.getState()
    const cap = window.innerWidth - MIN_CENTER - (st.treeOpen ? st.treeWidth : 0)
    setConciergeWidth(Math.min(st.conciergeWidth - d, cap))
  }, [setConciergeWidth])

  // Effective widths: if the window shrank below what the stored widths need,
  // scale the rails down proportionally so the center keeps MIN_CENTER.
  let effTree = treeOpen ? treeWidth : 0
  let effConcierge = conciergeOpen ? conciergeWidth : 0
  const railBudget = winW - MIN_CENTER
  if (effTree + effConcierge > railBudget && effTree + effConcierge > 0) {
    const scale = Math.max(0, railBudget) / (effTree + effConcierge)
    effTree = Math.round(effTree * scale)
    effConcierge = Math.round(effConcierge * scale)
  }

  // ─── Single-pane (narrow / mobile) ─────────────────────────────────────────
  if (isNarrow) {
    return (
      <div className="concierge-root flex h-dvh flex-col bg-background text-foreground">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
          <span className="text-sm font-semibold tracking-tight">Walker</span>
        </header>

        <main className="min-h-0 flex-1 overflow-hidden pb-14">
          <div className={`h-full overflow-y-auto ${pane === 'tree' ? '' : 'hidden'}`}>{treeEl}</div>
          <div className={`h-full overflow-y-auto ${pane === 'page' ? '' : 'hidden'}`}>{pageEl}</div>
          <div className={`h-full ${pane === 'concierge' ? '' : 'hidden'}`}>{railEl}</div>
        </main>

        <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-14 items-stretch border-t border-border bg-background/95 backdrop-blur-sm">
          {([
            { key: 'tree', label: 'Tree', Icon: FolderTree },
            { key: 'page', label: 'Page', Icon: FileText },
            { key: 'concierge', label: 'Concierge', Icon: MessageSquare },
          ] as const).map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setPane(key)}
              className={`flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
                pane === key ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              <Icon size={18} strokeWidth={pane === key ? 2 : 1.5} />
              {label}
            </button>
          ))}
        </nav>
      </div>
    )
  }

  // ─── Desktop ──────────────────────────────────────────────────────────────
  return (
    <div className="concierge-root flex h-dvh flex-col bg-background text-foreground">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
        <button
          onClick={() => setTreeOpen(!treeOpen)}
          title="Toggle tree"
          className={`rounded p-1.5 transition-colors hover:bg-muted ${treeOpen ? 'text-foreground' : 'text-muted-foreground'}`}
        >
          <PanelLeft size={16} strokeWidth={1.5} />
        </button>
        <div className="text-sm font-medium tracking-tight">Walker</div>
        <div className="flex-1" />
        <button
          onClick={() => setConciergeOpen(!conciergeOpen)}
          title="Toggle Concierge"
          className={`rounded p-1.5 transition-colors hover:bg-muted ${conciergeOpen ? 'text-foreground' : 'text-muted-foreground'}`}
        >
          <MessageSquare size={16} strokeWidth={1.5} />
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        {treeOpen && (
          <>
            <aside style={{ width: effTree }} className="shrink-0 overflow-y-auto bg-muted/20">
              {treeEl}
            </aside>
            <ResizableDivider onResize={onTreeResize} />
          </>
        )}
        <main className="min-w-0 flex-1 overflow-y-auto">{pageEl}</main>
        {conciergeOpen && (
          <>
            <ResizableDivider onResize={onConciergeResize} />
            <aside style={{ width: effConcierge }} className="shrink-0 overflow-hidden">
              {railEl}
            </aside>
          </>
        )}
      </div>
    </div>
  )
}
